import { createContext, useCallback, useContext, useState, ReactNode } from "react";

import { dateParser, datePlusDays } from "../libs/date_parser";

const getLastMonthDates = (mqty: number = 1) => {
  const currentDate = new Date();
  const year = currentDate.getUTCFullYear();
  const month = currentDate.getUTCMonth();

  const firstDayPrevMonth = new Date(Date.UTC(year, month - mqty, 1));
  const lastDayPrevMonth = new Date(Date.UTC(year, month, 0));

  return [firstDayPrevMonth, lastDayPrevMonth];
};

const getCurrentMonthDates = () => {
  const currentDate = new Date();
  const year = currentDate.getUTCFullYear();
  const month = currentDate.getUTCMonth();

  const firstDayCurrentMonth = new Date(Date.UTC(year, month, 1));
  const lastDayCurrentMonth = new Date(Date.UTC(year, month + 1, 0));

  return [firstDayCurrentMonth, lastDayCurrentMonth];
};

const getCurrentQuarterDates = () => {
  const currentDate = new Date();
  const year = currentDate.getUTCFullYear();
  const month = currentDate.getUTCMonth();

  const firstDayCurrentQuarter = new Date(Date.UTC(year, month - (month % 3), 1));
  const lastDayCurrentQuarter = new Date(Date.UTC(year, month + 1, 0));

  return [firstDayCurrentQuarter, lastDayCurrentQuarter];
};

const getCurrentYearDates = () => {
  const currentDate = new Date();
  const year = currentDate.getUTCFullYear();

  const firstDayCurrentYear = new Date(Date.UTC(year, 0, 1));
  const lastDayCurrentYear = new Date(Date.UTC(year + 1, 0, 0));

  return [firstDayCurrentYear, lastDayCurrentYear];
};

export const timeRangeOpts = [
  // NOTE: "value" can not be used for calculations, they are only for display or reference purposes
  { value: 7, text: "Last 7 days" },
  { value: 14, text: "Last 14 days" },
  { value: 28, text: "Last 28 days" },
  { value: 30, text: "Last month", dates: getLastMonthDates() },
  { value: 90, text: "Last 3 months", dates: getLastMonthDates(3) },
  { value: 180, text: "Last 6 months", dates: getLastMonthDates(6) },
  { value: 360, text: "Last 12 months", dates: getLastMonthDates(12) },
  { value: 32, text: "Current month", dates: getCurrentMonthDates() },
  { value: 92, text: "Current quarter", dates: getCurrentQuarterDates() },
  { value: 366, text: "Current year", dates: getCurrentYearDates() },
];

export const heatMapColors = (k: number) => {
  if (k < 0.2) return "#ffffff";
  else if (k < 0.4) return "#FFE5E5";
  else if (k < 0.6) return "#FFCCCC";
  else if (k < 0.8) return "#FF9999";
  else return "#FF6666";
};

export const timeIntervalOpts = [
  // { value: "hour", text: "Hour" },
  { value: "day", text: "Day" },
  { value: "week", text: "Week" },
  { value: "month", text: "Month" },
  // { value: "quarter", text: "Quarter" },
  // { value: "year", text: "Year" },
];

export const getCostsTimeRangeEnds = (timeRange: number) => {
  const timeRangeEnd =
    timeRangeOpts.find((c) => c.value === timeRange)?.dates?.[1] ?? dateParser(datePlusDays(-1), "yyyy-mm-dd");
  const timeRangeStart =
    timeRangeOpts.find((c) => c.value === timeRange)?.dates?.[0] ?? dateParser(datePlusDays(-timeRange), "yyyy-mm-dd");

  return { start: timeRangeStart, end: timeRangeEnd };
};

interface HeatmapData {
  node?: {
    [key: string]: {
      _id: string;
      weight: number;
    };
  };
}

interface TrendingData {
  previous: {
    start: string;
    end: string;
  };
  current: {
    start: string;
    end: string;
  };
  trending: number | null;
}

interface CostsContextType {
  timeRange: number;
  timeInterval: string;
  costsHeatmapOn: boolean;
  setCostsHeatmapOn: (on: boolean) => void;
  setTimeRange: (range: number) => void;
  setTimeInterval: (interval: string) => void;
  trending: TrendingData | null;
  setTrending: (trending: TrendingData) => void;
  loadingTrending: boolean;
  setLoadingTrending: (loading: boolean) => void;
  heatmap: HeatmapData;
  setHeatmap: (heatmap: HeatmapData) => void;
  loadingHeatMap: boolean;
  setLoadingHeatMap: (loading: boolean) => void;
  errorLoadingHeatmap: boolean;
  setErrorLoadingHeatmap: (error: boolean) => void;
}

const CostsContext = createContext<CostsContextType | undefined>(undefined);

export const CostsProvider = ({ children }: { children: ReactNode }) => {
  const [timeRange, setTimeRange] = useState<number>(28);
  const [timeInterval, setTimeInterval] = useState<string>("week");
  const [costsHeatmapOn, setCostsHeatmapOn] = useState<boolean>(false);
  const [heatmap, setHeatmap] = useState<HeatmapData>({});
  const [loadingTrending, setLoadingTrending] = useState(false);
  const [trending, setTrending] = useState<TrendingData | null>(null);
  const [loadingHeatMap, setLoadingHeatMap] = useState(false);
  const [errorLoadingHeatmap, setErrorLoadingHeatmap] = useState(false);

  return (
    <CostsContext.Provider
      value={{
        timeRange,
        timeInterval,
        setTimeRange,
        setTimeInterval,
        heatmap,
        setHeatmap,
        costsHeatmapOn,
        setCostsHeatmapOn,
        trending,
        setTrending,
        loadingTrending,
        setLoadingTrending,
        loadingHeatMap,
        setLoadingHeatMap,
        errorLoadingHeatmap,
        setErrorLoadingHeatmap,
      }}
    >
      {children}
    </CostsContext.Provider>
  );
};

export const useCosts = () => {
  const context = useContext(CostsContext);
  if (context === undefined) {
    throw new Error("useCosts must be used within a CostsProvider");
  }
  return context;
};
